import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { groupOf } from "@/lib/products";
import { waLink } from "@/lib/site";

const WhatsAppIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

export const ProductModal = ({ product, onClose }) => {
  const [qty, setQty] = useState("");
  const group = groupOf(product.category);
  const isRte = group.slug === "ready-to-eat";
  const packLabel = product.pack ? ` (${product.pack})` : "";
  const quoteTo = `/?product=${encodeURIComponent(product.name + packLabel)}&category=${encodeURIComponent(group.name)}${qty ? `&qty=${encodeURIComponent(qty)}` : ""}#enquire`;
  const waMsg = `Hello RBL EXIM, I am interested in ${product.name}${packLabel} (${group.name})${qty ? ` — Quantity: ${qty}` : ""}. Please share product availability, pricing, MOQ and export details. Thank you.`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-end justify-center bg-navy/70 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
      data-testid="product-modal"
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="grid max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-2xl bg-white sm:rounded-2xl md:grid-cols-2"
        data-lenis-prevent
      >
        <div className="relative bg-cream">
          <img
            src={product.image}
            alt={`${product.name} — RBL EXIM ${group.name}`}
            className="h-64 w-full object-cover md:h-full"
          />
          <span className="absolute left-4 top-4 rounded-full bg-navy px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-gold">
            Private Label Available
          </span>
        </div>
        <div className="flex flex-col p-7 md:p-9">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold-dark">
                {group.name}
              </p>
              <h3 className="mt-1.5 font-display text-2xl font-semibold tracking-tight text-navy md:text-3xl">
                {product.name}
              </h3>
              {product.brand && (
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-gold-dark">
                  by {product.brand}
                </p>
              )}
              {product.pack && (
                <p className="mt-1 text-sm font-medium text-navy/60">Pack Size: {product.pack}</p>
              )}
            </div>
            <button onClick={onClose} data-testid="product-modal-close" aria-label="Close"
              className="rounded-full border border-navy/15 p-2 text-navy transition-colors duration-300 hover:bg-navy hover:text-white">
              <X size={18} />
            </button>
          </div>

          <p className="mt-5 text-sm leading-relaxed text-navy/70">
            {group.productLine || group.description}
          </p>

          <div className="mt-5 space-y-2.5 border-t border-navy/10 pt-5 text-sm text-navy/70">
            <p>
              <span className="font-semibold text-navy">Packaging options:</span>{" "}
              {isRte ? "retail carton pack, bulk / food-service supply" : "retail and bulk export packaging"}
            </p>
            <p>
              <span className="font-semibold text-navy">Private label:</span> available — your brand, our product
            </p>
          </div>

          <div className="mt-5">
            <label htmlFor="product-modal-qty" className="text-xs font-semibold uppercase tracking-[0.16em] text-navy/60">
              Quantity Requirement
            </label>
            <input
              id="product-modal-qty"
              data-testid="product-modal-qty"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder="e.g. 1 x 20ft container / 5,000 packs"
              className="mt-1 w-full border-b border-navy/20 bg-transparent px-0 py-2.5 text-base text-navy placeholder:text-navy/35 focus:border-gold focus:outline-none transition-colors duration-300"
            />
          </div>

          <div className="mt-7 flex flex-col gap-2.5">
            <Link
              to={quoteTo}
              onClick={onClose}
              data-testid="product-modal-quote"
              className="flex items-center justify-center gap-2 rounded-full bg-navy py-3.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-gold hover:text-navy"
            >
              Request a Quote <ArrowUpRight size={15} />
            </Link>
            <a
              href={waLink(waMsg)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="product-modal-whatsapp"
              className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] py-3.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-[#1eb857]"
            >
              <WhatsAppIcon /> WhatsApp Enquiry
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
